import React from 'react';
import './GroupedDataTable.css';

export type GroupedTableColumn<Row> = {
  key: string;
  /** Header cell content. */
  header: React.ReactNode;
  /** Label shown before the value when rows stack (≤860px). Defaults to the header when it is a string. */
  label?: string;
  /** Hidden between 861 and 1180px so laptops never scroll sideways; its content should repeat in a sub-line. */
  optional?: boolean;
  align?: 'start' | 'end';
  /** Extra class on the body cells (e.g. 'num'). */
  className?: string;
  /** A fixed column width (CSS length) for table-layout: fixed. */
  width?: string;
  /** Makes the header a sort button; `direction` is set on the column the list is sorted by. */
  sort?: { direction: 'asc' | 'desc' | null; onSort: () => void };
  /** The header's aria-sort when the header brings its own sort control (ColumnHeaderMenu). */
  ariaSort?: 'asc' | 'desc' | null;
  render: (row: Row) => React.ReactNode;
};

function sortAria(direction: 'asc' | 'desc' | null | undefined): 'ascending' | 'descending' | undefined {
  if (direction === 'asc') return 'ascending';
  if (direction === 'desc') return 'descending';
  return undefined;
}

export type GroupedTableGroup = {
  key: string;
  label: string;
  /** Total rows in the group across every page (from the server), shown after the label. */
  count?: number | null;
  /** Extra context after the label, e.g. "Wed Oct 7" or "up to 30 days". */
  hint?: string | null;
};

export type GroupedTableFold = {
  title: string;
  count: number;
  body: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
};

type GroupedDataTableProps<Row> = {
  ariaLabel: string;
  columns: ReadonlyArray<GroupedTableColumn<Row>>;
  rows: ReadonlyArray<Row>;
  getRowKey: (row: Row) => string;
  /** Rows sharing a key are grouped under one header row; rows must already be sorted by group. */
  getGroupKey?: (row: Row) => string | null | undefined;
  groups?: ReadonlyArray<GroupedTableGroup>;
  /** 'late' adds the amber edge ("Late is a date"). */
  rowTone?: (row: Row) => 'late' | null | undefined;
  /** Extra class on a body row (e.g. a selected row). */
  rowClassName?: (row: Row) => string | null | undefined;
  /** The folded backlog row, drawn after the last row. */
  fold?: GroupedTableFold | null;
  /** Shown instead of the body when there are no rows. */
  emptyState?: React.ReactNode;
  /** Render this many rows first and offer "Show more" (long record line tables). */
  initialRowLimit?: number;
  /** A totals row after the body, by column key (columns left out stay empty). */
  footer?: { readonly [columnKey: string]: React.ReactNode } | null;
  className?: string;
};

function columnLabel<Row>(column: GroupedTableColumn<Row>): string | undefined {
  if (column.label) return column.label;
  return typeof column.header === 'string' ? column.header : undefined;
}

/**
 * A server-paged list table with group header rows, an amber edge for late rows and a folded
 * backlog row. Builds on `.data-table`; everything else is scoped to `.grouped-data-table`.
 * Below 861px rows stack into labelled cards; libraries switch to LibraryRecordCard at ≤720px.
 */
export function GroupedDataTable<Row>({
  ariaLabel,
  columns,
  rows,
  getRowKey,
  getGroupKey,
  groups,
  rowTone,
  rowClassName,
  fold,
  emptyState,
  initialRowLimit,
  footer,
  className,
}: GroupedDataTableProps<Row>) {
  const [rowLimit, setRowLimit] = React.useState<number | null>(initialRowLimit ?? null);
  React.useEffect(() => {
    setRowLimit(initialRowLimit ?? null);
  }, [initialRowLimit]);

  const groupsByKey = React.useMemo(() => {
    const map = new Map<string, GroupedTableGroup>();
    (groups ?? []).forEach((group) => map.set(group.key, group));
    return map;
  }, [groups]);

  const visibleRows = rowLimit != null ? rows.slice(0, rowLimit) : rows;
  const hiddenCount = rows.length - visibleRows.length;
  const columnCount = columns.length;

  const handleShowMore = React.useCallback(() => {
    setRowLimit((limit) => (limit == null ? null : limit + (initialRowLimit ?? limit)));
  }, [initialRowLimit]);

  // Consecutive rows with the same group key form one <tbody>, headed by a row-group header.
  const sections: { groupKey: string | null; rows: Row[] }[] = [];
  visibleRows.forEach((row) => {
    const groupKey = getGroupKey ? getGroupKey(row) ?? null : null;
    const last = sections[sections.length - 1];
    if (last && last.groupKey === groupKey) last.rows.push(row);
    else sections.push({ groupKey, rows: [row] });
  });

  const renderRow = (row: Row) => {
    const tone = rowTone ? rowTone(row) : null;
    return (
      <tr key={getRowKey(row)} className={rowClassName?.(row) || undefined} data-tone={tone || undefined}>
        {columns.map((column) => {
          const classes = [
            column.className,
            column.optional ? 'grouped-data-table__optional' : null,
            column.align === 'end' ? 'grouped-data-table__end' : null,
          ].filter(Boolean).join(' ');
          return (
            <td key={column.key} className={classes || undefined} data-label={columnLabel(column)}>
              {column.render(row)}
            </td>
          );
        })}
      </tr>
    );
  };

  const body = sections.map((section, index) => {
    const group = section.groupKey ? groupsByKey.get(section.groupKey) : undefined;
    const details = [group?.hint, group?.count != null ? group.count.toLocaleString() : null]
      .filter(Boolean)
      .join(' · ');
    return (
      <tbody key={`${section.groupKey ?? 'rows'}:${index}`}>
        {section.groupKey ? (
          <tr className="grouped-data-table__group">
            <th scope="rowgroup" colSpan={columnCount}>
              {group?.label ?? section.groupKey}
              {details ? <span className="grouped-data-table__group-details">{details}</span> : null}
            </th>
          </tr>
        ) : null}
        {section.rows.map(renderRow)}
      </tbody>
    );
  });

  return (
    <div className={className ? `grouped-data-table__wrap ${className}` : 'grouped-data-table__wrap'}>
      <table className="data-table grouped-data-table" aria-label={ariaLabel}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={[
                  column.optional ? 'grouped-data-table__optional' : null,
                  column.align === 'end' ? 'grouped-data-table__end' : null,
                ].filter(Boolean).join(' ') || undefined}
                style={column.width ? { width: column.width } : undefined}
                aria-sort={column.sort ? sortAria(column.sort.direction) : column.ariaSort !== undefined ? sortAria(column.ariaSort) ?? 'none' : undefined}
              >
                {column.sort ? (
                  <button
                    type="button"
                    className="grouped-data-table__sort"
                    data-direction={column.sort.direction ?? undefined}
                    onClick={column.sort.onSort}
                  >
                    {column.header}
                    <span className="grouped-data-table__sort-icon" aria-hidden="true">
                      {column.sort.direction === 'asc' ? '▲' : column.sort.direction === 'desc' ? '▼' : '↕'}
                    </span>
                  </button>
                ) : column.header}
              </th>
            ))}
          </tr>
        </thead>
        {rows.length === 0 && !(fold && fold.count > 0) ? (
          <tbody>
            <tr className="grouped-data-table__empty">
              <td colSpan={columnCount}>{emptyState ?? 'Nothing matches these filters.'}</td>
            </tr>
          </tbody>
        ) : body}
        {fold && fold.count > 0 ? (
          <tbody>
            <tr className="grouped-data-table__fold">
              <td colSpan={columnCount}>
                <div className="grouped-data-table__fold-layout">
                  <strong className="grouped-data-table__fold-title">
                    {fold.title}
                    <span className="grouped-data-table__group-details">
                      {fold.count.toLocaleString()} · {fold.expanded ? 'shown' : 'collapsed'}
                    </span>
                  </strong>
                  <span className="grouped-data-table__fold-body">{fold.body}</span>
                  <button
                    type="button"
                    className="btn xs"
                    onClick={fold.onToggle}
                    aria-expanded={fold.expanded}
                  >
                    {fold.expanded ? 'Hide' : 'Show'}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        ) : null}
        {footer && rows.length > 0 ? (
          <tfoot>
            <tr className="grouped-data-table__total">
              {columns.map((column) => {
                const classes = [
                  column.className,
                  column.optional ? 'grouped-data-table__optional' : null,
                  column.align === 'end' ? 'grouped-data-table__end' : null,
                ].filter(Boolean).join(' ');
                const content = footer[column.key];
                return (
                  <td key={column.key} className={classes || undefined} data-label={content != null ? columnLabel(column) : undefined}>
                    {content ?? null}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        ) : null}
      </table>
      {hiddenCount > 0 ? (
        <div className="grouped-data-table__more">
          <button type="button" className="btn xs" onClick={handleShowMore}>
            Show {Math.min(hiddenCount, initialRowLimit ?? hiddenCount).toLocaleString()} more
          </button>
          <span className="text-sm fg-muted">{hiddenCount.toLocaleString()} not shown yet</span>
        </div>
      ) : null}
    </div>
  );
}

export default GroupedDataTable;
